// Component: BrandSplash.jsx
// Purpose: The brand-reveal MOMENT wired for real use (design-system/preview/
//   logos.html). A full-screen dark overlay where the monogram stamps in
//   (BrandReveal) then the wordmark/greeting fades up. Two modes:
//   - "welcome" (post-sign-in celebration): fixed hold of `duration`.
//   - "splash" (cold-start): LOAD-AWARE — pass `ready` (e.g. auth check done).
//     Dismisses as soon as the app is ready, but never before `floor` (so it
//     can't flicker) and never after `maxWait` (so it can't hang). No artificial
//     delay: on a fast boot it's just the ~floor brand flash.
//   Plays once; gated by the caller (session flag etc.).

import React, { useEffect, useState } from "react";
import { BrandReveal } from "./BrandReveal";
import { cx } from "./cx";
import brandMark from "../../data/logo-icon-light.png";
import useThemeColor from "../../hooks/useThemeColor";

export function BrandSplash({
  mode = "splash",
  name,
  ready,               // undefined => fixed-timer (welcome); boolean => load-aware
  duration = 1800,     // welcome hold
  floor = 300,         // load-aware: earliest dismiss (~when the monogram is formed; snappy)
  maxWait = 1000,      // load-aware: latest dismiss (backstop — hand off to the app loader fast)
  onDone,
}) {
  const loadAware = ready !== undefined;
  const [leaving, setLeaving] = useState(false);
  const [floorPassed, setFloorPassed] = useState(!loadAware);

  // The splash is a fixed dark radial (see .ds-splash) — match the status bar to
  // it so the cold-start moment doesn't flash a light bar in light mode.
  useThemeColor("#0d0d0d");

  //--> Timers: welcome holds `duration`; load-aware arms a floor (earliest
  //    dismiss) + a cap (latest dismiss, in case `ready` never arrives).
  useEffect(() => {
    if (!loadAware) {
      const t = setTimeout(() => setLeaving(true), duration);
      return () => clearTimeout(t);
    }
    const tFloor = setTimeout(() => setFloorPassed(true), floor);
    const tCap = setTimeout(() => setLeaving(true), maxWait);
    return () => {
      clearTimeout(tFloor);
      clearTimeout(tCap);
    };
  }, [loadAware, duration, floor, maxWait]);

  //--> Load-aware: leave the moment the app is ready AND the floor has passed.
  useEffect(() => {
    if (loadAware && ready && floorPassed) setLeaving(true);
  }, [loadAware, ready, floorPassed]);

  //--> After the exit animation, tell the caller to unmount / set the seen flag.
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => onDone?.(), 200);
    return () => clearTimeout(t);
  }, [leaving, onDone]);

  return (
    <div className={cx("ds-splash", leaving && "leaving")} role="status" aria-live="polite">
      <div className="ds-splash-inner">
        <BrandReveal
          size={112}
          //--> white monogram on the dark splash (matches the DS reference .icon-white)
          icon={<img src={brandMark} alt="DeeTrade" width={64} height={64} style={{ display: "block", filter: "brightness(0) invert(1)" }} />}
        />
        {mode === "welcome" ? (
          <>
            <div className="ds-splash-title">Welcome{name ? `, ${name}` : ""}</div>
            <div className="ds-splash-sub">Setting up your portfolio…</div>
          </>
        ) : (
          <>
            <div className="ds-splash-word">deetrade</div>
            <div className="ds-splash-sub">trade smart.</div>
          </>
        )}
      </div>
    </div>
  );
}

export default BrandSplash;
